import type {
  ProviderLinkedExam, ProviderAvailableExam, ProviderExamBulkItem,
} from '../../types/providerExam';

export interface EditableExamRow {
  examId: number;
  examName: string;
  esocialCode: string | null;
  tussCode: string | null;
  price: number | null;
  cost: number | null;
  turnaroundDays: number | null;
  observations: string | null;
  _origin: 'existing' | 'new';
  _dirty: boolean;
}

export function rowFromLinked(e: ProviderLinkedExam): EditableExamRow {
  return {
    examId: e.examId, examName: e.examName, esocialCode: e.esocialCode, tussCode: e.tussCode,
    price: e.price, cost: e.cost, turnaroundDays: e.turnaroundDays, observations: e.observations,
    _origin: 'existing', _dirty: false,
  };
}

export function rowFromAvailable(e: ProviderAvailableExam): EditableExamRow {
  return {
    examId: e.id, examName: e.examName, esocialCode: e.esocialCode, tussCode: e.tussCode,
    price: null, cost: null, turnaroundDays: null, observations: null,
    _origin: 'new', _dirty: false,
  };
}

/** Returns an error message, or null if the row is valid for saving. */
export function validateRow(row: EditableExamRow): string | null {
  if (row.price == null || !Number.isFinite(row.price) || row.price <= 0) {
    return 'Valor a cobrar é obrigatório e deve ser maior que zero';
  }
  if (row.cost != null && (!Number.isFinite(row.cost) || row.cost < 0)) {
    return 'Custo deve ser maior ou igual a zero';
  }
  if (row.turnaroundDays != null &&
      (!Number.isInteger(row.turnaroundDays) || row.turnaroundDays < 0 || row.turnaroundDays > 90)) {
    return 'Prazo deve ser um inteiro entre 0 e 90 dias';
  }
  return null;
}

/** Rows that must be sent on "save all": new rows and edited existing rows. */
export function rowsToSave(rows: EditableExamRow[]): EditableExamRow[] {
  return rows.filter((r) => r._origin === 'new' || r._dirty);
}

const collator = new Intl.Collator('pt-BR', { sensitivity: 'base' });

/** Insere a linha mantendo a lista ordenada por examName (A→Z, ignora acento/caixa). */
export function insertSorted(rows: EditableExamRow[], row: EditableExamRow): EditableExamRow[] {
  const next = [...rows];
  const idx = next.findIndex((r) => collator.compare(r.examName, row.examName) > 0);
  if (idx === -1) next.push(row); else next.splice(idx, 0, row);
  return next;
}

export function collectBulkItems(rows: EditableExamRow[]): ProviderExamBulkItem[] {
  const items: ProviderExamBulkItem[] = [];
  for (const r of rowsToSave(rows)) {
    if (r.price == null) continue; // caller validates first; guard keeps price non-null without a cast
    items.push({
      examId: r.examId,
      price: r.price,
      cost: r.cost,
      turnaroundDays: r.turnaroundDays,
      observations: r.observations,
    });
  }
  return items;
}
